import React, { useCallback, useEffect, useRef } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";
import { FinancialDocument } from "@common/types/financialDocument";
import { useUpdateFinancialDocumentMetadataMutation } from "@api/financialDocument.api";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { HStack } from "@design-system/HStack/HStack";
import { splitFileName } from "@design-system/FileTypeIcon/fileType";
import styles from "./FinancialDocumentRenameModal.module.scss";

const renameDocumentFormSchema = z.object({
    fileName: z.string().trim().min(1, "File name cannot be empty"),
});

type RenameDocumentFormValues = z.infer<typeof renameDocumentFormSchema>;

const fields = createFormikFields<RenameDocumentFormValues>();

interface Props {
    show: boolean;
    financialDocument: FinancialDocument;
    onClose(): void;
    onSuccess?(updatedDocument: FinancialDocument): void;
}

export const FinancialDocumentRenameModal: React.FC<Props> = ({
    show,
    financialDocument,
    onClose,
    onSuccess,
}) => {
    const updateMetadataMutation =
        useUpdateFinancialDocumentMetadataMutation(financialDocument);
    const inputRef = useRef<HTMLInputElement>(null);
    const { name, extension } = splitFileName(financialDocument.fileName);

    const onSubmit = useCallback(
        async (values: RenameDocumentFormValues) => {
            const trimmedFileName = values.fileName.trim();
            const fullFileName = extension
                ? `${trimmedFileName}.${extension}`
                : trimmedFileName;

            const updatedDocument = await updateMetadataMutation.mutateAsync({
                fileName: fullFileName,
            });

            onSuccess?.(updatedDocument);
            onClose();
        },
        [extension, updateMetadataMutation, onSuccess, onClose],
    );

    const formik = useFormik<RenameDocumentFormValues>({
        initialValues: {
            fileName: name,
        },
        validate: validateFormikWithZodSchema(renameDocumentFormSchema),
        validateOnMount: true,
        enableReinitialize: true,
        onSubmit: submitHelper({
            handler: onSubmit,
            loading: updateMetadataMutation.isPending,
        }),
    });

    const isLoading = updateMetadataMutation.isPending;

    useEffect(() => {
        if (show && inputRef.current) {
            const timer = setTimeout(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [show]);

    return (
        <PlainModal show={show} onHide={onClose}>
            <StandardModalHeader>Rename document</StandardModalHeader>
            <FormikProvider value={formik}>
                <Form
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            formik.handleSubmit();
                        }
                    }}
                >
                    <fields.Text
                        name="fileName"
                        label="Name"
                        size="xl"
                        ref={inputRef}
                        placeholder="Enter document name..."
                        className={styles.nameField}
                    />
                    {extension && (
                        <div className="text-sm text-gray-500 mt-1">
                            Extension: .{extension}
                        </div>
                    )}
                    <HStack as="footer" justify="end" gap={12}>
                        <Button
                            variant="secondary"
                            size="xl"
                            onClick={onClose}
                            disabled={formik.isSubmitting || isLoading}
                        >
                            Cancel
                        </Button>

                        <ButtonWithLoader
                            disabled={!formik.isValid}
                            loading={formik.isSubmitting || isLoading}
                            variant="primary"
                            type="submit"
                            size="xl"
                        >
                            Rename
                        </ButtonWithLoader>
                    </HStack>
                </Form>
            </FormikProvider>
        </PlainModal>
    );
};
